import { useState, useEffect, useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { arrayMove } from '@dnd-kit/sortable'
import useApi from '../../hooks/useApi'
import buildQueryString from '../../utils/api/buildQueryString'
import useTranslation from '../../hooks/useTranslation'

function computeRank(prev, next) {
    if (prev == null && next == null) return 1000
    if (prev == null) return (next ?? 1000) - 1000
    if (next == null) return prev + 1000
    return (prev + next) / 2
}

export default function useKanban({ parentId, parentEntity, selectorConfig, areaConfig, assigneeFilterId, assigneeIdField }) {
    const { getMe, putMe } = useApi()
    const queryClient = useQueryClient()
    const { t } = useTranslation()

    const subEntity = areaConfig.subEntity

    // Query keys are scoped to parentEntity to avoid collisions across kanban views
    const LIST_QUERY_KEY    = [parentEntity, 'list']
    const DETAIL_QUERY_KEY  = [parentEntity, 'detail', parentId]
    const TASKS_QUERY_KEY   = [parentEntity, 'tasks', parentId]

    // Build the parent list URL from selector config (same params used by the Connect control)
    const listQueryString = selectorConfig?.entityParams
        ? buildQueryString(selectorConfig.entityParams)
        : ''

    // ── Parent list (for auto-select) ──────────────────────────────────────
    const { data: parentListResult } = useQuery({
        queryKey: LIST_QUERY_KEY,
        queryFn:  () => getMe(`${parentEntity}${listQueryString}`),
        staleTime: 60_000
    })

    const parentList = parentListResult?.data ?? []

    // ── Selected parent detail (name + status shown in header) ─────────────
    const { data: parentDetailResult } = useQuery({
        queryKey: DETAIL_QUERY_KEY,
        queryFn:  () => getMe(`${parentEntity}/${parentId}`),
        enabled:  !!parentId,
        staleTime: 30_000
    })

    const selectedParent = parentDetailResult?.data ?? null

    // ── Tasks for the selected parent ───────────────────────────────────────
    const { data: tasksResult, isLoading: tasksLoading } = useQuery({
        queryKey: TASKS_QUERY_KEY,
        queryFn:  () => getMe(`${parentEntity}/${parentId}/${subEntity}/?orderBy=${areaConfig.stackRankField}&direction=asc`),
        enabled:  !!parentId,
        staleTime: 0
    })

    // Local tasks state — starts from server data, updated optimistically on drag
    const [tasks, setTasks] = useState([])

    // Clear tasks immediately when parent changes to avoid showing stale data
    useEffect(() => {
        setTasks([])
    }, [parentId])

    useEffect(() => {
        if (tasksResult?.data) {
            setTasks(tasksResult.data)
        }
    }, [tasksResult])

    // Group tasks by status for rendering
    const tasksByStatus = useCallback((swimlanes) => {
        const map = {}
        swimlanes.forEach(s => { map[s.statusValue] = [] })
        tasks.forEach(task => {
            if (assigneeFilterId && assigneeIdField && task[assigneeIdField] !== assigneeFilterId) return
            const status = task[areaConfig.statusField]
            if (map[status] !== undefined) {
                map[status].push(task)
            }
        })
        return map
    }, [tasks, areaConfig.statusField, assigneeFilterId, assigneeIdField])

    // Invalidate and re-fetch tasks (called after modal create/edit)
    const invalidateTasks = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: TASKS_QUERY_KEY })
    }, [queryClient, parentId])

    // ── Drag-and-drop handler ───────────────────────────────────────────────
    const onDragEnd = useCallback((event, swimlanes) => {
        const { active, over } = event
        if (!over || active.id === over.id) return

        const activeTaskId    = active.id
        const overTaskId      = over.id
        const activeSwimlaneId = active.data.current?.swimlaneId
        const overSwimlaneId   = over.data.current?.swimlaneId

        if (!activeSwimlaneId || !overSwimlaneId) return

        const grouped = tasksByStatus(swimlanes)

        if (activeSwimlaneId === overSwimlaneId) {
            // ── Reorder within same swimlane ──────────────────────────────
            const col      = [...grouped[activeSwimlaneId]]
            const oldIdx   = col.findIndex(t => t.id === activeTaskId)
            const newIdx   = col.findIndex(t => t.id === overTaskId)
            if (oldIdx === -1 || newIdx === -1) return

            const reordered = arrayMove(col, oldIdx, newIdx)
            const prev      = reordered[newIdx - 1]?.stackRank ?? null
            const next      = reordered[newIdx + 1]?.stackRank ?? null
            const newRank   = computeRank(prev, next)

            const updated = { ...reordered[newIdx], stackRank: newRank }
            reordered[newIdx] = updated
            setTasks(prev => {
                const others = prev.filter(t => t[areaConfig.statusField] !== activeSwimlaneId)
                return [...others, ...reordered]
            })

            putMe(`${parentEntity}/${parentId}/${subEntity}`, { stackRank: newRank }, activeTaskId)

        } else {
            // ── Move between swimlanes ────────────────────────────────────
            const srcCol    = [...grouped[activeSwimlaneId]]
            const dstCol    = [...grouped[overSwimlaneId]]
            const activeTask = srcCol.find(t => t.id === activeTaskId)
            if (!activeTask) return

            const overIdx   = dstCol.findIndex(t => t.id === overTaskId)
            const insertIdx = overIdx === -1 ? dstCol.length : overIdx
            const prevRank  = dstCol[insertIdx - 1]?.stackRank ?? null
            const nextRank  = dstCol[insertIdx]?.stackRank ?? null
            const newRank   = computeRank(prevRank, nextRank)

            const newSwimlane = swimlanes.find(s => s.id === overSwimlaneId)
            const newStatus = newSwimlane?.statusValue
            if (!newStatus) return

            const patch = { [areaConfig.statusField]: newStatus, stackRank: newRank }
            if (areaConfig.statusNameField && newSwimlane.title) {
                patch[areaConfig.statusNameField] = t(newSwimlane.title)
            }

            const updated = { ...activeTask, ...patch }
            setTasks(prev => prev.map(t => t.id === activeTaskId ? updated : t))

            putMe(`${parentEntity}/${parentId}/${subEntity}`, patch, activeTaskId)
        }
    }, [tasksByStatus, areaConfig.statusField, parentEntity, parentId, subEntity, putMe])

    // Compute next stackRank for a new task appended to a swimlane
    const nextStackRankForSwimlane = useCallback((statusValue, swimlanes) => {
        const grouped = tasksByStatus(swimlanes)
        const col     = grouped[statusValue] ?? []
        const last    = col[col.length - 1]?.stackRank ?? null
        return last != null ? last + 1000 : 1000
    }, [tasksByStatus])

    return {
        parentList,
        selectedParent,
        tasks,
        tasksLoading,
        tasksByStatus,
        onDragEnd,
        invalidateTasks,
        nextStackRankForSwimlane
    }
}
